import React, { useEffect, useState } from "react";
import { Box, Typography, Button, Stack, Paper, Chip } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import { useNavigate } from "react-router-dom";
import { authHeader, getSession } from "../auth";

// Formate une date ISO en date+heure lisible (fr-CH) — pas de fuseau
// horaire particulier à gérer, le serveur écrit déjà en heure locale via
// NOW() / new Date().
function formatDate(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleString("fr-CH", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

// Page en lecture / gestion des courses parallèles archivées. Une course
// archivée conserve TOUTES ses données de chronométrage (voir
// finals.js — POST /api/finals/archive/:category) : cette page permet de
// les retrouver et, si besoin, de les faire réapparaître dans la liste
// active (/parallel) via "Désarchiver".
export default function ParallelArchives() {
    const [archived, setArchived] = useState([]);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);
    const [unarchivingCategory, setUnarchivingCategory] = useState(null);
    const navigate = useNavigate();
    const session = getSession();
    const role = session?.role;

    const loadArchived = () => {
        setLoading(true);
        fetch("/api/finals/archived", { headers: { ...authHeader() } })
            .then(async (r) => {
                if (!r.ok) {
                    const data = await r.json().catch(() => ({}));
                    throw new Error(data.error || `HTTP ${r.status}`);
                }
                return r.json();
            })
            .then((data) => {
                setArchived(data);
                setError("");
            })
            .catch((err) => setError(err.message))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        loadArchived();
    }, []);

    const unarchiveCategory = async (category) => {
        const confirmed = window.confirm(
            `Désarchiver "${category}" ?\n\nElle réapparaîtra dans la liste des courses parallèles actives.`
        );
        if (!confirmed) return;

        setError("");
        setUnarchivingCategory(category);
        try {
            const res = await fetch(`/api/finals/unarchive/${category}`, {
                method: "POST",
                headers: { ...authHeader() },
            });

            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                setError(data.error || `HTTP ${res.status}`);
                return;
            }

            loadArchived();
        } catch (err) {
            setError(err.message);
        } finally {
            setUnarchivingCategory(null);
        }
    };

    if (role !== "chronometreur") {
        return (
            <Box p={3}>
                <Typography color="error">
                    Cette page est réservée au rôle chronométreur.
                </Typography>
            </Box>
        );
    }

    return (
        <Box p={3}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
                <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/parallel")}>
                    Courses parallèles
                </Button>
            </Box>

            <Typography variant="h4" mb={1}>
                Courses archivées
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
                Ces courses ne figurent plus dans la liste des courses parallèles actives, mais
                toutes leurs données de chronométrage (bracket, résultats de chaque duel, écarts,
                photofinish, décisions du jury) sont conservées. Le bracket complet reste
                consultable en cliquant sur une catégorie ; "Désarchiver" la remet dans la liste
                active.
            </Typography>

            {error && (
                <Typography color="error" sx={{ mb: 2 }}>
                    Erreur : {error}
                </Typography>
            )}

            {loading ? (
                <Typography color="text.secondary">Chargement…</Typography>
            ) : archived.length === 0 ? (
                <Typography color="text.secondary">Aucune course archivée pour l'instant.</Typography>
            ) : (
                <Stack spacing={2}>
                    {archived.map((item) => (
                        <Paper key={item.category} sx={{ p: 2 }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    flexWrap: "wrap",
                                    gap: 1,
                                }}
                            >
                                <Box>
                                    <Typography variant="h6">{item.category}</Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Archivée le {formatDate(item.archived_at)} · dernière mise à
                                        jour le {formatDate(item.updated_at)}
                                    </Typography>
                                </Box>

                                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                                    <Chip
                                        label="Voir le bracket"
                                        clickable
                                        onClick={() => navigate(`/brackets/${item.category}`)}
                                    />
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        color="success"
                                        startIcon={<UnarchiveIcon fontSize="small" />}
                                        disabled={unarchivingCategory === item.category}
                                        onClick={() => unarchiveCategory(item.category)}
                                    >
                                        {unarchivingCategory === item.category
                                            ? "Désarchivage…"
                                            : "Désarchiver"}
                                    </Button>
                                </Box>
                            </Box>
                        </Paper>
                    ))}
                </Stack>
            )}
        </Box>
    );
}